import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TradingChart from '@/pages/trading/components/TradingChart';
import { supabase } from '@/lib/supabase';

const timeOptions = [
  { seconds: 180, profit: 5, label: '180s / 5%' },
  { seconds: 300, profit: 10, label: '300s / 10%' },
  { seconds: 540, profit: 15, label: '540s / 15%' },
  { seconds: 720, profit: 20, label: '720s / 20%' },
];

interface Instrument {
  id: string;
  symbol: string;
  name: string;
  ask: number;
  bid: number;
  changePercent: number;
}

interface RecentOrder {
  id: string;
  symbol: string;
  side: string;
  volume: number;
  entry_price: number;
  status: string;
  created_at: string;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function Trading() {
  const [amount, setAmount] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState(timeOptions[0]);
  const [symbolId, setSymbolId] = useState<string>('');
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [walletId, setWalletId] = useState<string | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingOrder, setPendingOrder] = useState<null | { side: 'up' | 'down'; amount: number; profit: number; seconds: number }>(null);
  const [activeTrade, setActiveTrade] = useState<null | {
    orderId: string;
    side: 'up' | 'down';
    amount: number;
    profit: number;
    seconds: number;
    entryPrice: number;
    symbol: string;
    remainingSeconds: number;
    currentPrice: number;
  }>(null);
  const [tradeResult, setTradeResult] = useState<null | {
    result: 'win' | 'loss';
    profit: number;
    balance: number;
    symbol: string;
    side: 'up' | 'down';
    amount: number;
  }>(null);

  const selected = instruments.find((s) => s.id === symbolId) ?? instruments[0];

  const finalProfit = useMemo(() => {
    const num = Number(amount.replace(/\./g, '').replace(/,/g, ''));
    if (!num || num <= 0) return 0;
    return Math.round(num * (selectedTime.profit / 100));
  }, [amount, selectedTime]);

  const totalAmount = useMemo(() => {
    const num = Number(amount.replace(/\./g, '').replace(/,/g, ''));
    return !num || num <= 0 ? 0 : num;
  }, [amount]);

  const showToast = (msg: string, duration = 3000) => {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  };

  async function fetchData() {
    try {
      setLoading(true);
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id ?? null;
      setUserId(uid);

      // Load active instruments + latest prices
      const [{ data: symbolsData }, { data: pricesData }] = await Promise.all([
        supabase.from('market_symbols').select('id, symbol, name').eq('is_active', true).order('symbol'),
        supabase.from('market_prices').select('symbol_id, bid, ask, change_percent_24h'),
      ]);

      const priceMap = new Map<string, { bid: number; ask: number; changePercent: number }>();
      (pricesData || []).forEach((p: any) => {
        priceMap.set(p.symbol_id, {
          bid: Number(p.bid),
          ask: Number(p.ask),
          changePercent: Number(p.change_percent_24h) || 0,
        });
      });

      const insts: Instrument[] = (symbolsData || [])
        .filter((s: any) => s.symbol === 'XAUUSD')
        .filter((s: any) => priceMap.has(s.id))
        .map((s: any) => {
          const px = priceMap.get(s.id)!;
          return {
            id: s.id,
            symbol: s.symbol,
            name: s.name,
            ask: px.ask,
            bid: px.bid,
            changePercent: px.changePercent,
          };
        });

      setInstruments(insts);
      if (insts.length > 0 && !insts.some((i) => i.id === symbolId)) {
        setSymbolId(insts[0].id);
      }

      if (uid) {
        const [{ data: walletRaw }, { data: ordersRaw }] = await Promise.all([
          supabase.rpc('get_user_wallet_json', { p_user_id: uid }),
          supabase.rpc('get_user_orders_json', { p_user_id: uid }),
        ]);

        const wallets = Array.isArray(walletRaw) ? walletRaw : JSON.parse((walletRaw as string) || '[]');
        const ordersData = Array.isArray(ordersRaw) ? ordersRaw : JSON.parse((ordersRaw as string) || '[]');

        if (wallets[0]) {
          setWalletId(wallets[0].id);
          setBalance(Number(wallets[0].balance) || 0);
        }

        const symbolNameMap = new Map<string, string>();
        (symbolsData || []).forEach((s: any) => symbolNameMap.set(s.id, s.symbol));

        setRecentOrders(
          (ordersData || []).slice(0, 10).map((o: any) => ({
            id: o.id,
            symbol: symbolNameMap.get(o.symbol_id) || '—',
            side: o.side,
            volume: Number(o.volume) || 0,
            entry_price: Number(o.entry_price) || 0,
            status: o.status,
            created_at: o.created_at,
          })),
        );
      }
    } catch (e: any) {
      showToast(e.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer for active trade
  useEffect(() => {
    if (!activeTrade || activeTrade.remainingSeconds <= 0) return;
    const id = setInterval(() => {
      setActiveTrade((prev) => {
        if (!prev) return null;
        const newRemaining = prev.remainingSeconds - 1;
        const currentPrice = selected
          ? prev.side === 'up'
            ? selected.ask
            : selected.bid
          : prev.currentPrice;
        if (newRemaining <= 0) {
          return { ...prev, remainingSeconds: 0, currentPrice };
        }
        return { ...prev, remainingSeconds: newRemaining, currentPrice };
      });
    }, 1000);
    return () => clearInterval(id);
  }, [activeTrade?.orderId, selected]);

  // Auto-settle when countdown reaches 0
  useEffect(() => {
    if (!activeTrade || activeTrade.remainingSeconds > 0) return;
    let settled = false;
    async function settle() {
      if (settled) return;
      settled = true;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error('Unauthorized');
        const resp = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/auto-settle-order`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ order_id: activeTrade.orderId }),
          },
        );
        const result = await resp.json();
        if (!resp.ok || !result.success) {
          throw new Error(result.error || 'Quyết toán thất bại');
        }
        if (result.balance !== undefined) {
          setBalance(result.balance);
        }
        setRecentOrders((prev) =>
          prev.map((o) =>
            o.id === activeTrade.orderId
              ? { ...o, status: result.result === 'win' ? 'won' : 'lost' }
              : o,
          ),
        );
        setTradeResult({
          result: result.result,
          profit: result.profit,
          balance: result.balance,
          symbol: activeTrade.symbol,
          side: activeTrade.side,
          amount: activeTrade.amount,
        });
        setActiveTrade(null);
      } catch (e: any) {
        showToast(e.message || 'Không thể quyết toán lệnh');
        setActiveTrade(null);
      }
    }
    settle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrade?.remainingSeconds]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) { setAmount(''); return; }
    setAmount(Number(raw).toLocaleString('vi-VN'));
  };

  const handleTrade = (side: 'up' | 'down') => {
    if (activeTrade) {
      showToast('Bạn đang có lệnh đang chạy, vui lòng chờ kết quả');
      return;
    }
    if (!userId) {
      showToast('Vui lòng đăng nhập để giao dịch');
      return;
    }
    if (totalAmount <= 0) {
      showToast('Vui lòng nhập số tiền giao dịch', 2500);
      return;
    }
    if (totalAmount > balance) {
      showToast('Số dư không đủ để thực hiện giao dịch');
      return;
    }
    setPendingOrder({ side, amount: totalAmount, profit: finalProfit, seconds: selectedTime.seconds });
  };

  const confirmTrade = async () => {
    if (!pendingOrder || !userId || !walletId || !selected) return;
    setSubmitting(true);
    try {
      const side = pendingOrder.side;
      const entryPrice = side === 'up' ? selected.ask : selected.bid;

      // Ghi lệnh + trừ số dư + ghi sổ cái qua function bypass (atomic)
      const { data: result, error: tradeErr } = await supabase.rpc('open_trade_order_json', {
        p_user_id: userId,
        p_symbol_id: selected.id,
        p_side: side,
        p_volume: pendingOrder.amount,
        p_entry_price: entryPrice,
        p_profit_amount: pendingOrder.profit,
        p_duration_seconds: pendingOrder.seconds,
      });

      if (tradeErr) throw tradeErr;
      if (result && result.error) {
        throw new Error(
          result.error === 'Insufficient balance'
            ? 'Số dư không đủ để thực hiện giao dịch'
            : result.error
        );
      }

      const newBalance = Number(result?.balance) || (balance - pendingOrder.amount);
      const orderId = result?.order_id as string;
      const sideLabel = side === 'up' ? 'Mua lên' : 'Mua xuống';

      setBalance(newBalance);
      setRecentOrders((prev) => [
        {
          id: orderId,
          symbol: selected.symbol,
          side: sideLabel,
          volume: pendingOrder.amount,
          entry_price: entryPrice,
          status: 'open',
          created_at: new Date().toISOString(),
        },
        ...prev.slice(0, 9),
      ]);
      setActiveTrade({
        orderId,
        side,
        amount: pendingOrder.amount,
        profit: pendingOrder.profit,
        seconds: pendingOrder.seconds,
        entryPrice,
        symbol: selected.symbol,
        remainingSeconds: pendingOrder.seconds,
        currentPrice: entryPrice,
      });
      setPendingOrder(null);
      setAmount('');
    } catch (e: any) {
      showToast(e.message || 'Giao dịch thất bại, vui lòng thử lại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 text-sm text-foreground-600 mb-4 md:mb-6">
            <Link to="/" className="hover:text-primary-300 cursor-pointer">Trang chủ</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">Giao dịch</span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 md:mb-6">Giao dịch vàng</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2 space-y-6">
              <TradingChart symbol={selected?.symbol} basePrice={selected?.ask ?? 2500} />

              <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">Sổ lệnh gần đây</h3>

                {loading && (
                  <div className="flex items-center justify-center py-10">
                    <div className="w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {!loading && recentOrders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10">
                    <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mb-3">
                      <i className="ri-file-list-3-line text-xl text-foreground-500"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-700">Chưa có lệnh nào</p>
                    <p className="text-xs text-foreground-500 mt-1">Các lệnh giao dịch của bạn sẽ hiển thị tại đây</p>
                  </div>
                )}

                {!loading && recentOrders.length > 0 && (
                  <div className="space-y-2">
                    {recentOrders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between py-2 border-b border-background-200/40 last:border-0 text-sm">
                        <span className="text-foreground-600 whitespace-nowrap">{formatTime(o.created_at)}</span>
                        <span className="text-foreground-800 font-semibold whitespace-nowrap">{o.symbol}</span>
                        <span className={`font-semibold ${o.side === 'up' || o.side === 'Mua lên' ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {o.side === 'up' ? 'Mua lên' : o.side === 'down' ? 'Mua xuống' : o.side}
                        </span>
                        <span className="text-foreground-800 whitespace-nowrap">{o.volume.toLocaleString('vi-VN')}đ</span>
                        <span className="font-semibold text-foreground-950 whitespace-nowrap">{o.entry_price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          o.status === 'won'
                            ? 'bg-emerald-100 text-emerald-700'
                            : o.status === 'lost'
                              ? 'bg-rose-100 text-rose-700'
                              : o.status === 'open'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-background-200 text-foreground-600'
                        }`}>
                          {o.status === 'won' ? 'Thắng' : o.status === 'lost' ? 'Thua' : o.status === 'open' ? 'Đang chạy' : o.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-background-200/60 bg-background-100 p-4 md:p-5 h-fit text-foreground-950">
              <div className="mb-4">
                <label className="block text-xs font-semibold text-foreground-400 mb-1.5">Công cụ giao dịch</label>
                <select
                  value={selected?.id ?? ''}
                  onChange={(e) => setSymbolId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md bg-background-200 border border-background-300 text-sm text-foreground-950 focus:outline-none focus:border-primary-500"
                >
                  {instruments.length === 0 && <option value="">Đang tải...</option>}
                  {instruments.map((s) => (
                    <option key={s.id} value={s.id}>{s.symbol} — {s.name}</option>
                  ))}
                </select>
                {selected && (
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-foreground-400">Giá {selected.symbol}</span>
                    <span className={`font-semibold ${selected.changePercent >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {selected.ask.toLocaleString('en-US', { maximumFractionDigits: 2 })} ({selected.changePercent >= 0 ? '+' : ''}{selected.changePercent}%)
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-end justify-between mb-1">
                <span className="text-xs font-semibold text-foreground-400">Số tiền (VND)</span>
                <span className="text-xs text-foreground-500">Tối đa <span className="font-semibold text-foreground-950">{balance.toLocaleString('vi-VN')} VND</span></span>
              </div>
              <div className="relative mb-5">
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Nhập số tiền (VND)"
                  value={amount}
                  onChange={handleAmountChange}
                  disabled={!!activeTrade}
                  className="w-full px-3 py-3 rounded-md bg-background-200 border border-background-300 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400 pointer-events-none">VND</span>
              </div>

              <p className="text-xs font-semibold text-foreground-400 mb-3">Thời lượng / lợi nhuận</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {timeOptions.map((opt) => (
                  <button
                    key={opt.seconds}
                    onClick={() => setSelectedTime(opt)}
                    disabled={!!activeTrade}
                    className={`py-2.5 rounded-lg text-xs font-bold whitespace-nowrap cursor-pointer transition-colors border ${
                      selectedTime.seconds === opt.seconds
                        ? 'bg-primary-500 text-background-950 border-primary-500'
                        : 'bg-transparent text-foreground-950 border-background-300 hover:border-background-500'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between mb-5">
                <span className="text-xs text-foreground-400">Lợi nhuận cuối cùng</span>
                <span className="text-sm font-bold text-foreground-950">{finalProfit.toLocaleString('vi-VN')} VND</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleTrade('up')}
                  disabled={submitting || !!activeTrade}
                  className="py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-colors bg-emerald-500 text-background-950 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  MUA LÊN
                </button>
                <button
                  onClick={() => handleTrade('down')}
                  disabled={submitting || !!activeTrade}
                  className="py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-colors bg-rose-500 text-background-950 hover:bg-rose-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  MUA XUỐNG
                </button>
              </div>

              <p className="mt-4 text-xs text-foreground-500 text-center">
                {activeTrade
                  ? `⏳ Lệnh ${activeTrade.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'} đang chạy... còn ${activeTrade.remainingSeconds}s`
                  : userId
                    ? 'Số dư sẽ được trừ ngay khi mở lệnh.'
                    : 'Bạn cần đăng nhập để thực hiện giao dịch thực.'}
              </p>
            </div>
          </div>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-background-950 text-foreground-50 text-sm font-semibold border border-background-700 animate-fade-in">
          {toast}
        </div>
      )}

      {pendingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setPendingOrder(null)}>
          <div className="rounded-xl bg-background-100 p-5 md:p-6 max-w-sm w-full mx-4 border border-background-200/60" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-foreground-950 mb-4">Xác nhận lệnh</h3>
            <div className="space-y-2 mb-5 text-sm">
              <div className="flex justify-between">
                <span className="text-foreground-600">Công cụ</span>
                <span className="font-semibold text-foreground-950">{selected?.symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Loại lệnh</span>
                <span className={`font-bold ${pendingOrder.side === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {pendingOrder.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Số tiền</span>
                <span className="font-semibold text-foreground-950">{pendingOrder.amount.toLocaleString('vi-VN')}đ</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Thời lượng</span>
                <span className="font-semibold text-foreground-950">{pendingOrder.seconds}s</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-600">Lợi nhuận dự kiến</span>
                <span className="font-semibold text-primary-500">+{pendingOrder.profit.toLocaleString('vi-VN')}đ</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setPendingOrder(null)} className="py-2.5 rounded-md text-sm font-semibold cursor-pointer border border-background-300/60 text-foreground-700 hover:bg-background-200/50">Hủy</button>
              <button onClick={confirmTrade} disabled={submitting} className={`py-2.5 rounded-md text-sm font-semibold cursor-pointer text-background-950 disabled:opacity-50 ${pendingOrder.side === 'up' ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-rose-500 hover:bg-rose-400'}`}>
                {submitting ? 'Đang xử lý...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active trade countdown overlay */}
      {activeTrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="rounded-2xl bg-background-100 p-6 md:p-8 max-w-sm w-full mx-4 border border-background-200/60 text-center">
            <h3 className="text-base font-bold text-foreground-950 mb-1">
              {activeTrade.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'} {activeTrade.symbol}
            </h3>
            <p className="text-xs text-foreground-500 mb-6">
              {activeTrade.amount.toLocaleString('vi-VN')}đ · {activeTrade.seconds}s
            </p>

            {/* Circular countdown */}
            <div className="relative w-36 h-36 mx-auto mb-6">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="oklch(var(--background-200))" strokeWidth="6" />
                <circle
                  cx="50" cy="50" r="42" fill="none"
                  stroke="oklch(var(--primary-500))"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={2 * Math.PI * 42 * (1 - activeTrade.remainingSeconds / activeTrade.seconds)}
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className="text-3xl font-bold text-foreground-950 tabular-nums">
                  {activeTrade.remainingSeconds}s
                </span>
              </div>
            </div>

            {/* Price comparison */}
            <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
              <div className="rounded-lg bg-background-200/50 p-3">
                <p className="text-xs text-foreground-500 mb-0.5">Giá mở lệnh</p>
                <p className="font-bold text-foreground-950">{activeTrade.entryPrice.toLocaleString('en-US', { maximumFractionDigits: 2 })}</p>
              </div>
              <div className="rounded-lg bg-background-200/50 p-3">
                <p className="text-xs text-foreground-500 mb-0.5">Giá hiện tại</p>
                <p className={`font-bold ${
                  (activeTrade.side === 'up' && activeTrade.currentPrice > activeTrade.entryPrice) ||
                  (activeTrade.side === 'down' && activeTrade.currentPrice < activeTrade.entryPrice)
                    ? 'text-emerald-500'
                    : (activeTrade.side === 'up' && activeTrade.currentPrice < activeTrade.entryPrice) ||
                      (activeTrade.side === 'down' && activeTrade.currentPrice > activeTrade.entryPrice)
                      ? 'text-rose-500'
                      : 'text-foreground-950'
                }`}>
                  {activeTrade.currentPrice.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            <p className="text-xs text-foreground-500">
              {(activeTrade.side === 'up' && activeTrade.currentPrice > activeTrade.entryPrice) ||
               (activeTrade.side === 'down' && activeTrade.currentPrice < activeTrade.entryPrice)
                ? 'Đang thắng 🟢'
                : (activeTrade.side === 'up' && activeTrade.currentPrice < activeTrade.entryPrice) ||
                  (activeTrade.side === 'down' && activeTrade.currentPrice > activeTrade.entryPrice)
                  ? 'Đang thua 🔴'
                  : 'Hòa ⚪'}
            </p>
          </div>
        </div>
      )}

      {/* Trade result modal */}
      {tradeResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className={`rounded-2xl p-6 md:p-8 max-w-sm w-full mx-4 border text-center ${
            tradeResult.result === 'win'
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-rose-50 border-rose-200'
          }`}>
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              tradeResult.result === 'win' ? 'bg-emerald-100' : 'bg-rose-100'
            }`}>
              <i className={`text-2xl ${
                tradeResult.result === 'win' ? 'ri-trophy-line text-emerald-600' : 'ri-emotion-sad-line text-rose-600'
              }`}></i>
            </div>

            <h3 className={`text-xl font-bold mb-2 ${
              tradeResult.result === 'win' ? 'text-emerald-700' : 'text-rose-700'
            }`}>
              {tradeResult.result === 'win' ? 'Chúc mừng!' : 'Rất tiếc!'}
            </h3>
            <p className={`text-sm mb-4 ${
              tradeResult.result === 'win' ? 'text-emerald-600' : 'text-rose-600'
            }`}>
              {tradeResult.result === 'win'
                ? 'Bạn đã thắng giao dịch'
                : 'Bạn đã thua giao dịch này'}
            </p>

            <div className={`rounded-xl p-4 mb-6 ${
              tradeResult.result === 'win' ? 'bg-emerald-100/60' : 'bg-rose-100/60'
            }`}>
              <p className="text-xs text-foreground-500 mb-1">
                {tradeResult.side === 'up' ? 'MUA LÊN' : 'MUA XUỐNG'} {tradeResult.symbol}
              </p>
              <p className={`text-2xl font-bold ${
                tradeResult.result === 'win' ? 'text-emerald-600' : 'text-rose-600'
              }`}>
                {tradeResult.result === 'win' ? '+' : ''}{tradeResult.profit.toLocaleString('vi-VN')} VND
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                Số dư hiện tại: {tradeResult.balance.toLocaleString('vi-VN')} VND
              </p>
            </div>

            <button
              onClick={() => setTradeResult(null)}
              className={`w-full py-3 rounded-full font-bold text-sm cursor-pointer transition-colors ${
                tradeResult.result === 'win'
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                  : 'bg-rose-500 text-white hover:bg-rose-600'
              }`}
            >
              Tiếp tục giao dịch
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}